import React from 'react';
import { Box, TextField, Checkbox, MenuItem } from '@material-ui/core';
import CreateContentField from './CreateContentField';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import { Boost, OrderType } from './types';
import { useDebouncedState } from '../useDebouncedState';

const ORDER_TYPE_NONE = '__NONE__';

type Props = {
  boost: Boost;
  index: number;
  errors: any;
  onChange: (index: number, field: keyof Boost, value: any) => void;
  onRemove: (index: number) => void;
};

const BoostRow = React.memo(
  ({ boost, index, errors, onChange, onRemove }: Props) => {
    const { value: localBoost, setValue: setLocalBoost } = useDebouncedState({
      value: boost,
      onChange: (newValue) => {
        Object.keys(newValue).forEach((key) => {
          const field = key as keyof Boost;
          if (newValue[field] !== boost[field]) {
            onChange(index, field, newValue[field]);
          }
        });
      },
      delay: 300,
    });

    const handleChange = (field: keyof Boost, value: any) => {
      setLocalBoost({ ...localBoost, [field]: value });
    };

    return (
      <div className='content__fields-item'>
        <div className='boost-fields-row'>
          <Box width={'100%'} display={'flex'}>
            {/* ACTIVE */}
            <CreateContentField
              fieldTitle='Active'
              field={
                <Checkbox
                  checked={localBoost.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
              }
            />

            {/* TITLE */}
            <CreateContentField
              fieldTitle='Title'
              field={
                <TextField
                  size='small'
                  variant='outlined'
                  value={localBoost.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  error={!!errors?.title}
                  helperText={errors?.title}
                />
              }
            />

            {/* LABEL */}
            <CreateContentField
              fieldTitle='Label'
              field={
                <TextField
                  variant='outlined'
                  size='small'
                  value={localBoost.label}
                  onChange={(e) => handleChange('label', e.target.value)}
                />
              }
            />
            {/* Order Type */}
            <CreateContentField
              fieldTitle='Order Type'
              field={
                <TextField
                  select
                  variant='outlined'
                  size='small'
                  value={localBoost.orderType ?? ORDER_TYPE_NONE}
                  onChange={(e) =>
                    handleChange(
                      'orderType',
                      e.target.value === ORDER_TYPE_NONE
                        ? undefined
                        : (e.target.value as OrderType),
                    )
                  }
                >
                  <MenuItem value={ORDER_TYPE_NONE}>No Type</MenuItem>
                  <MenuItem value='WIN'>WIN</MenuItem>
                  <MenuItem value='DEAL'>DEAL</MenuItem>
                  <MenuItem value='DISCOUNT'>DISCOUNT</MenuItem>
                  <MenuItem value='OFFER'>OFFER</MenuItem>
                </TextField>
              }
            />
            {/* SOURCE */}
            <CreateContentField
              fieldTitle='Source'
              field={
                <TextField
                  select
                  variant='outlined'
                  size='small'
                  value={localBoost.source}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('source', e.target.value)
                  }
                >
                  <MenuItem value='ALL'>ALL</MenuItem>
                </TextField>
              }
            />
          </Box>
          <Box width={'100%'} display={'flex'}>
            {/* ORDER COST */}
            <CreateContentField
              fieldTitle='Order cost'
              field={
                <TextField
                  variant='outlined'
                  type='number'
                  size='small'
                  value={localBoost.orderCost}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('orderCost', e.target.value)
                  }
                  error={!!errors?.orderCost}
                  helperText={errors?.orderCost}
                />
              }
            />

            {/* BOOST COST */}
            <CreateContentField
              fieldTitle='Boost cost'
              field={
                <TextField
                  variant='outlined'
                  type='number'
                  size='small'
                  value={localBoost.boostCost}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('boostCost', e.target.value)
                  }
                />
              }
            />

            {/* TICKETS */}
            <CreateContentField
              fieldTitle='Tickets'
              field={
                <TextField
                  variant='outlined'
                  type='number'
                  size='small'
                  value={localBoost.boostTickets}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('boostTickets', e.target.value)
                  }
                />
              }
            />

            {/* DISCOUNT */}
            <CreateContentField
              fieldTitle='Discount'
              field={
                <TextField
                  variant='outlined'
                  size='small'
                  value={localBoost.boostDiscount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('boostDiscount', e.target.value)
                  }
                  error={!!errors?.boostDiscount}
                  helperText={errors?.boostDiscount}
                />
              }
            />

            {/* DELETE */}
            <button
              type='button'
              className='content__delete-btn'
              onClick={() => onRemove(index)}
            >
              <RemoveIcon />
            </button>
          </Box>
        </div>
      </div>
    );
  },
);

export default BoostRow;
